/**
 * SystemHealthDashboard
 *
 * Real-time system health page for admins. Calls GET /api/v1/monitoring/digest
 * and renders the same payload shape produced by the scheduled daily digest:
 *  - Overall status banner (green/yellow/red)
 *  - Per-module health cards with check details
 *  - Last 24 hours numeric summary
 *
 * Epic: MVP 1.29.5 - Daily Health Digest
 * Story: SCRUM-2176 (1.29.5-05, 5pt)
 *
 * AC-05: route /admin/system-health
 * AC-06: per-module green/yellow/red indicators
 * AC-07: last-24h numbers section
 * AC-09: app-level data only (no infrastructure)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity as ActivityIcon, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';

const STATUS_META = {
  green: { color: '#16a34a', bg: '#dcfce7', label: 'Healthy' },
  yellow: { color: '#ca8a04', bg: '#fef9c3', label: 'Warning' },
  red: { color: '#dc2626', bg: '#fee2e2', label: 'Critical' },
};

const LAST_24H_LABELS = {
  failed_jobs: 'Failed jobs',
  dead_letters: 'Dead letters',
  ops_alerts_sent: 'Ops alerts sent',
  system_alerts_sent: 'System alerts sent',
  blacklist_additions: 'Blacklist additions',
  threat_events: 'Threat events',
  frontend_errors: 'Frontend errors',
  email_failures: 'Email failures',
  sms_failures: 'SMS failures',
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] ?? STATUS_META.green;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        backgroundColor: meta.bg,
        color: meta.color,
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
      }}
    >
      {meta.label}
    </span>
  );
}

function ModuleCard({ module }) {
  const meta = STATUS_META[module.status] ?? STATUS_META.green;
  return (
    <div
      style={{
        border: '1px solid var(--color-border, #e5e7eb)',
        borderLeft: `4px solid ${meta.color}`,
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: 'white',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: '16px', textTransform: 'capitalize' }}>
          {module.module.replace(/_/g, ' ')}
        </h3>
        <StatusPill status={module.status} />
      </div>
      {Array.isArray(module.checks) && module.checks.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {module.checks.map((c, i) => (
            <li key={`${c.name}-${i}`} style={{ fontSize: '13px', padding: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
              <span>
                <span
                  aria-label={`status-${c.status}`}
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: (STATUS_META[c.status] ?? STATUS_META.green).color,
                    marginRight: 8,
                  }}
                />
                <strong>{c.name}</strong>
                {c.message ? ` — ${c.message}` : ''}
              </span>
              {c.value !== undefined && c.value !== null && (
                <span style={{ color: '#6b7280' }}>{String(c.value)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SystemHealthDashboard() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDigest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest('/api/v1/monitoring/digest');
      setDigest(data);
    } catch (err) {
      setError(err.message || 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDigest();
  }, [fetchDigest]);

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const overallMeta = digest ? STATUS_META[digest.overallStatus] ?? STATUS_META.green : STATUS_META.green;

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="system-health"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null,
      }}
    >
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>System Health</h1>
        <button
          onClick={fetchDigest}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: loading ? '#d1d5db' : '#0d9488',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" style={{ padding: 16, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading && !digest && (
        <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading system health…</div>
      )}

      {digest && (
        <>
          <div
            role="status"
            style={{
              padding: '20px',
              backgroundColor: overallMeta.bg,
              borderRadius: '8px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: '14px', color: '#374151' }}>Overall status — {digest.date}</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: overallMeta.color, textTransform: 'uppercase' }}>
                {overallMeta.label}
              </div>
            </div>
            <ActivityIcon size={48} color={overallMeta.color} />
          </div>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: 12 }}>Module Health</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(digest.modules || []).map((m) => (
                <ModuleCard key={m.module} module={m} />
              ))}
            </div>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: 12 }}>Last 24 Hours</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(LAST_24H_LABELS).map(([key, label]) => (
                <div key={key} style={{ padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'white' }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{digest.last24h?.[key] ?? 0}</div>
                </div>
              ))}
            </div>
          </section>

          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 16 }}>
            Generated at {digest.generated_at}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

export default SystemHealthDashboard;
